import type { PropsWithChildren } from 'react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { truncateText } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import type { PropsWithClassName } from '../../../types';

const FALLBACK_IMAGE =
  'https://static.elfsight.com/icons/app-audio-player-empty-multicolor.svg';
const FALLBACK_IMAGE_RADIO =
  'https://static.elfsight.com/icons/app-radio-player-station-placeholder-image-multicolor.svg';

interface ImageProps extends PropsWithChildren<PropsWithClassName> {
  trackName?: string;
  link?: string;
  showImage?: boolean;
}

export function Image({
  trackName = '',
  link,
  className,
  children,
  showImage = true
}: ImageProps) {
  const { sourceType } = useSettings();
  const fallbackImage =
    sourceType === 'radio' ? FALLBACK_IMAGE_RADIO : FALLBACK_IMAGE;
  const derivedLink = link ?? fallbackImage;

  const [url, setUrl] = useState(derivedLink);

  useEffect(() => {
    setUrl(derivedLink);
  }, [derivedLink]);

  return (
    <ImageContainer className={className}>
      {showImage && (
        <Container>
          <ImageComponent
            key={url}
            src={url}
            alt={truncateText(trackName)}
            onError={() => setUrl(fallbackImage)}
          />
        </Container>
      )}

      {children}
    </ImageContainer>
  );
}

const ImageContainer = styled.div`
  position: relative;
  flex-shrink: 0;
  overflow: hidden;
`;

const Container = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 6px;

  &:before {
    content: '';
    position: absolute;
    z-index: 10;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 1px solid rgba(17, 17, 17, 0.1);
    border-radius: inherit;
  }
`;
const ImageComponent = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  object-fit: cover;
  user-select: none;
`;
